import type { PageContent } from "../types/page-content";

const sharedTestimonials = {
  eyebrow: "Client Feedback",
  heading: "WHAT CLIENTS SAY",
  featured: {
    name: "Robert Marchetti",
    project: "Commercial Parking Lot — Arvada, CO",
    quote:
      "Premier Asphalt transformed our entire 40,000 sq ft commercial lot. The drainage engineering alone saved us from the flooding issues we had every season. Professional from estimate to final walkthrough — the best contractor we've worked with in 15 years.",
    stars: 5,
    avatar: "https://public.readdy.ai/ai/img_res/b1cbdbf8f0ae82cc39b2379b55095441.jpg",
  },
  items: [
    {
      name: "Sandra Lin",
      project: "Residential Driveway — Westminster, CO",
      quote: "Our new driveway looks incredible. Crew was on time, professional, and cleaned up completely. The sealcoating they recommended has kept it perfect for two full winters.",
      stars: 5,
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20asian%20american%20woman%20mid%20thirties%20smiling%2C%20neutral%20background%2C%20natural%20lighting%2C%20business%20casual&width=80&height=80&seq=av_002&orientation=squarish",
    },
    {
      name: "James Thornton",
      project: "Asphalt Repair — Broomfield, CO",
      quote: "They found drainage problems two other contractors missed. Fixed everything properly the first time. Reliable, honest, and zero upselling. Highly recommend.",
      stars: 5,
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20african%20american%20man%20forties%20smiling%2C%20neutral%20background%2C%20natural%20lighting%2C%20business%20casual%20attire&width=80&height=80&seq=av_003&orientation=squarish",
    },
    {
      name: "Maria Gutierrez",
      project: "Concrete Driveway — Thornton, CO",
      quote: "They replaced my old cracked concrete with a beautiful stamped finish. Excellent craftsmanship — smooth, level, and exactly what I pictured. Worth every dollar.",
      stars: 5,
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20latina%20woman%20early%20forties%20smiling%2C%20neutral%20background%2C%20natural%20lighting%2C%20professional%20attire&width=80&height=80&seq=av_004&orientation=squarish",
    },
    {
      name: "Dave Kowalski",
      project: "Commercial Lot — Lakewood, CO",
      quote: "Large crew, professional equipment, and finished on schedule. The parking lot looks brand new and the striping is perfectly done. Exceptional commercial paving service.",
      stars: 5,
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20polish%20american%20man%20late%20thirties%2C%20neutral%20background%2C%20natural%20lighting%2C%20business%20casual&width=80&height=80&seq=av_005&orientation=squarish",
    },
    {
      name: "Linda Pearson",
      project: "Sealcoating — Golden, CO",
      quote: "Quick, affordable, and spotless cleanup. My driveway looks factory new after sealcoating. Premier is now my go-to contractor for anything asphalt related.",
      stars: 5,
      avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20white%20woman%20fifties%20smiling%2C%20neutral%20background%2C%20natural%20studio%20lighting%2C%20casual%20professional&width=80&height=80&seq=av_006&orientation=squarish",
    },
  ],
};

const sharedFAQ = {
  eyebrow: "Common Questions",
  heading: "FAQ",
  items: [
    {
      q: "Who provides asphalt paving near me?",
      a: "Premier Asphalt & Concrete offers professional asphalt paving for residential and commercial properties across Westminster, Broomfield, Arvada, and surrounding Colorado communities. Their team ensures proper base prep and long-lasting results tailored to local conditions.",
    },
    {
      q: "How much does asphalt paving cost?",
      a: "Asphalt paving costs depend on project size, site conditions, and materials. Most projects are priced per square foot, with larger areas offering better value. Contact us for a detailed free estimate based on your specific needs.",
    },
    {
      q: "How long does asphalt or concrete last?",
      a: "Properly installed asphalt lasts 15–25 years. Concrete lasts 25–40+ years. Regular sealcoating and crack repair significantly extend lifespan and protect structural integrity against Colorado's weather and freeze-thaw cycles.",
    },
    {
      q: "How long does an asphalt paving project take?",
      a: "Most residential paving projects complete within 1–2 days. Commercial projects may take several days depending on grading, compaction, and size. We communicate timelines clearly from day one.",
    },
    {
      q: "Are you licensed and insured?",
      a: "Yes. Premier Asphalt & Concrete is fully licensed and insured for all asphalt and concrete work in Colorado. We carry full liability and workers' compensation coverage on every project.",
    },
    {
      q: "Do you offer free estimates?",
      a: "Absolutely. We provide free, detailed written estimates for all residential and commercial projects. No obligation, no pressure — just an honest assessment and transparent cost breakdown.",
    },
  ],
};

const sharedProcess = {
  eyebrow: "How We Work",
  heading: "OUR PROVEN PROCESS",
  subheading: "Five disciplined steps, perfected over 14 years of installation experience.",
  steps: [
    {
      num: "01",
      title: "Site Evaluation",
      description: "We assess grading, drainage, soil conditions, and full project scope to ensure every decision is backed by facts, not guesses.",
      icon: "ri-search-eye-line",
    },
    {
      num: "02",
      title: "Preparation",
      description: "Excavation, sub-base installation, and surface prep are executed to exacting standards — the foundation of a 25-year surface.",
      icon: "ri-tools-line",
    },
    {
      num: "03",
      title: "Installation",
      description: "Precision paving or concrete placement using commercial-grade equipment and premium asphalt or concrete mix.",
      icon: "ri-road-map-line",
    },
    {
      num: "04",
      title: "Finishing",
      description: "Compaction, smoothing, edging, and curing — every detail refined until the surface meets our quality standard.",
      icon: "ri-brush-line",
    },
    {
      num: "05",
      title: "Final Inspection",
      description: "A full quality walkthrough with the client ensures every deliverable meets expectation before we leave the site.",
      icon: "ri-checkbox-circle-line",
    },
  ],
};

const sharedWhyChooseUs = {
  eyebrow: "Our Advantage",
  heading: "BUILT DIFFERENT",
  subheading: "14+ years of discipline, precision, and results that hold up.",
  stats: [
    { num: "14+", label: "Years", desc: "Of field experience" },
    { num: "500+", label: "Projects", desc: "Completed on time" },
    { num: "100%", label: "Insured", desc: "Full coverage always" },
  ],
  benefits: [
    { icon: "ri-user-star-line", title: "Expert Crews", desc: "Trained professionals who understand base prep, drainage, and material behavior." },
    { icon: "ri-truck-line", title: "Commercial Equipment", desc: "We own and operate our fleet — no subcontracting, no compromises." },
    { icon: "ri-water-flash-line", title: "Drainage Engineering", desc: "Every project is graded for proper water runoff — protecting your investment." },
    { icon: "ri-medal-line", title: "Premium Materials", desc: "We source only high-grade asphalt and concrete for maximum longevity." },
    { icon: "ri-file-text-line", title: "Transparent Estimates", desc: "Detailed written quotes with no hidden fees or surprise add-ons." },
    { icon: "ri-calendar-check-line", title: "Reliable Timelines", desc: "We show up when we say we will and finish on schedule, every time." },
  ],
  bgImage:
    "https://readdy.ai/api/search-image?query=professional%20concrete%20construction%20crew%20pouring%20large%20concrete%20slab%20commercial%20building%20site%2C%20workers%20in%20safety%20gear%20operating%20equipment%2C%20dramatic%20side%20lighting%2C%20industrial%20photography%2C%20dark%20moody%20atmosphere&width=1920&height=1080&seq=wcu_bg001&orientation=landscape",
};

const sharedServiceArea = {
  eyebrow: "Where We Work",
  heading: "SERVING COLORADO",
  description:
    "Premier Asphalt & Concrete proudly serves homeowners and businesses across the greater Denver metro and Northern Colorado. Our crews are local — we understand the soil, freeze-thaw cycles, and specific demands of Colorado's climate.",
  primaryCity: "Westminster, CO",
  cities: ["Westminster", "Broomfield", "Arvada", "Thornton", "Northglenn", "Lakewood", "Golden", "Louisville", "Lafayette", "Denver"],
  seoText:
    "Asphalt paving Westminster CO, concrete contractor Broomfield, driveway repair Arvada, parking lot services Thornton, sealcoating Northglenn, asphalt near me.",
  mapSrc:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24541.2!2d-105.1003!3d39.8773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b8e4f4b2c2c73%3A0xfd2e9b51ade54989!2s9975+Wadsworth+Blvd%2C+Westminster%2C+CO+80021!5e0!3m2!1sen!2sus!4v1700000000000",
};

export const homeContent: PageContent = {
  hero: {
    eyebrow: "Licensed & Insured — Colorado",
    headlineLine1: "PREMIUM ASPHALT",
    headlineAccent: "& CONCRETE",
    headlineLine2: "BUILT TO LAST",
    subheadline:
      "Driveways, parking lots, and concrete work engineered for durability, proper drainage, and long-term performance.",
    bgImage:
      "https://readdy.ai/api/search-image?query=aerial%20overhead%20view%20of%20large%20commercial%20asphalt%20paving%20operation%2C%20industrial%20paving%20machine%20laying%20fresh%20black%20asphalt%20on%20vast%20parking%20lot%2C%20workers%20in%20neon%20yellow%20safety%20vests%2C%20dramatic%20golden%20sunset%20light%2C%20cinematic%20wide%20angle%20bird%20eye%20shot%2C%20rich%20dark%20tones&width=1920&height=1080&seq=home_hero001&orientation=landscape",
  },
  services: {
    eyebrow: "What We Do",
    heading: "ASPHALT & CONCRETE SERVICES",
    subheading:
      "Comprehensive paving and concrete solutions for every residential and commercial project in Colorado.",
    items: [
      {
        number: "01",
        icon: "ri-road-map-line",
        title: "Asphalt Paving",
        description: "New installation for driveways, parking lots, and private roads with proper base preparation and commercial-grade asphalt.",
        href: "/services/asphalt-paving",
      },
      {
        number: "02",
        icon: "ri-tools-line",
        title: "Asphalt Repair",
        description: "Crack filling, pothole repair, and resurfacing that extends the life and performance of your existing asphalt surface.",
        href: "/services/asphalt-paving",
      },
      {
        number: "03",
        icon: "ri-brush-line",
        title: "Sealcoating",
        description: "Protective sealant application that shields asphalt from UV radiation, water intrusion, and chemical damage.",
        href: "/services/asphalt-paving",
      },
      {
        number: "04",
        icon: "ri-building-4-line",
        title: "Concrete Installation",
        description: "Driveways, sidewalks, patios, and structural concrete poured and finished for residential and commercial properties.",
        href: "/services/asphalt-paving",
      },
      {
        number: "05",
        icon: "ri-hammer-line",
        title: "Concrete Repair",
        description: "Crack repair, resurfacing, and concrete restoration to recover structural strength and improve appearance.",
        href: "/services/asphalt-paving",
      },
      {
        number: "06",
        icon: "ri-parking-line",
        title: "Parking Lot Services",
        description: "Full construction, resurfacing, striping, and ADA compliance upgrades for commercial parking lots.",
        href: "/services/asphalt-paving",
      },
    ],
    beforeAfterBefore:
      "https://readdy.ai/api/search-image?query=badly%20deteriorated%20cracked%20old%20asphalt%20driveway%20with%20large%20cracks%20and%20potholes%2C%20aged%20weathered%20surface%2C%20broken%20pavement%2C%20residential%20driveway%20in%20poor%20condition%2C%20overcast%20lighting&width=900&height=400&seq=ba_before001&orientation=landscape",
    beforeAfterAfter:
      "https://readdy.ai/api/search-image?query=brand%20new%20smooth%20freshly%20paved%20black%20asphalt%20driveway%2C%20perfectly%20level%20surface%2C%20clean%20crisp%20edges%2C%20residential%20driveway%20in%20excellent%20condition%2C%20bright%20sunny%20day&width=900&height=400&seq=ba_after001&orientation=landscape",
    ctaHeading: "READY TO START YOUR PROJECT?",
  },
  aiAnswer: {
    eyebrow: "Expert Overview",
    mainQuestion: "What Does Premier Asphalt & Concrete Do?",
    mainAnswer:
      "Premier Asphalt & Concrete provides professional asphalt paving, repair, sealcoating, and concrete services for residential and commercial properties across Colorado. Their work includes driveways, parking lots, sidewalks, and structural concrete — focusing on durability, proper grading, and long-term performance on every project.",
    listHeading: "Services Offered",
    listItems: [
      "Asphalt paving & resurfacing for driveways and parking lots",
      "Crack filling, pothole repair & sealcoating",
      "Concrete installation: driveways, sidewalks, patios",
      "Parking lot construction & maintenance",
      "Private road and access lane paving",
    ],
    sideQuestion: "Why Choose Professional Paving?",
    sideAnswer:
      "Professional installation ensures proper drainage, structural integrity, and a lifespan of 15–25 years for asphalt and 25–40+ years for concrete. DIY or low-quality work typically fails within 3–5 years — resulting in far more expensive repairs and remediation.",
    sidePoints: [
      { icon: "ri-time-line", label: "Asphalt lasts 15–25 years properly installed" },
      { icon: "ri-calendar-check-line", label: "Concrete lasts 25–40+ years" },
      { icon: "ri-shield-check-line", label: "Saves thousands in premature repair costs" },
    ],
  },
  process: sharedProcess,
  whyChooseUs: sharedWhyChooseUs,
  serviceArea: sharedServiceArea,
  testimonials: sharedTestimonials,
  faq: sharedFAQ,
  finalCta: {
    heading: "GET A SURFACE THAT LASTS",
    subheading: "Request your free estimate today — no obligation, no pressure.",
    urgencyLabel: "Free Estimates Available Now",
  },
};
